import { Link, useNavigate } from 'react-router-dom';
import { auth } from '../../../lib/firebase';
import { getUserProfile } from '../../../services/auth';

const HeroSection = () => {
  const navigate = useNavigate();
  const handleSellClick = async () => {
    const user = auth.currentUser;
    if (!user) {
      navigate('/auth/sign-in?next=/seller-dashboard?action=create&type=item');
      return;
    }
    try {
      const profile = await getUserProfile(user.uid);
      const status = (profile as any)?.status || 'pending';
      const isApproved = (profile as any)?.isVerified === true || status === 'active' || !!(profile as any)?.approvalDate;
      if (isApproved) {
        navigate('/seller-dashboard?action=create&type=item');
      } else {
        navigate('/sell?step=verification&resume=docs');
      }
    } catch {
      navigate('/seller-dashboard?action=create&type=item');
    }
  };
  return (
    <section 
      className="relative bg-gradient-to-br from-teal-50 via-blue-50 to-indigo-50 py-20 lg:py-32 overflow-hidden"
      style={{
        backgroundImage: `url('https://readdy.ai/api/search-image?query=Modern%20university%20campus%20with%20diverse%20students%20walking%20with%20backpacks%20and%20books%2C%20bright%20sunny%20day%2C%20contemporary%20architecture%2C%20green%20spaces%2C%20vibrant%20campus%20life%2C%20students%20collaborating%20and%20studying%20outdoors%2C%20clean%20minimalist%20background%20perfect%20for%20text%20overlay&width=1200&height=800&seq=campus-hero-bg&orientation=landscape')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundBlendMode: 'overlay'
      }}
    >
      <div className="absolute inset-0 bg-white/80"></div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="text-left">
            <div className="inline-flex items-center space-x-2 bg-teal-100 text-teal-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <i className="ri-shield-check-fill"></i>
              <span>Trusted by students across campus</span>
            </div>

            <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Your Campus
              <span className="text-teal-600 block">Marketplace</span>
              <span className="text-gray-700 text-2xl lg:text-3xl block mt-2">Buy • Sell • Trade</span>
            </h1>

            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Buy, sell, and trade with fellow students. Compare prices, find trusted sellers,
              and discover the best deals right here on campus.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Link 
                to="/marketplace"
                className="inline-flex items-center justify-center px-8 py-4 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-shopping-bag-line mr-2"></i>
                Start Shopping
              </Link>
              
              <button 
                type="button"
                onClick={handleSellClick}
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-teal-600 text-teal-600 font-semibold rounded-lg hover:bg-teal-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-add-circle-line mr-2"></i>
                Sell Your Items
              </button>
            </div>
            
            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-6 pt-8 border-t border-gray-200">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">2.5K+</div>
                <div className="text-sm text-gray-600">Active Students</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">15K+</div>
                <div className="text-sm text-gray-600">Items Sold</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">4.9★</div>
                <div className="text-sm text-gray-600">Safety Rating</div>
              </div>
            </div>
          </div>
          
          {/* Right Content - Featured Deal Preview */}
          <div className="relative">
            <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md mx-auto">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center">
                  <i className="ri-store-2-fill text-white"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Featured Deal</h3>
                  <p className="text-sm text-green-600">● Available now</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="bg-gray-100 rounded-lg p-3">
                  <p className="text-sm text-gray-700">iPhone 13 Pro, 128GB &mdash; excellent condition</p>
                </div>

                <div className="bg-teal-50 rounded-lg p-3">
                  <p className="text-sm text-gray-700">
                    Best deal: <strong>₦170,000</strong> from Sarah (4.9★, verified student).
                    Located 2 blocks from your dorm and ready to meet today.
                  </p>
                </div>

                <div className="flex space-x-2">
                  <Link to="/marketplace" className="flex-1 bg-teal-600 text-white py-2 px-4 rounded-lg text-sm font-medium whitespace-nowrap cursor-pointer text-center">
                    View deal
                  </Link>
                  <Link to="/marketplace" className="flex-1 border border-gray-300 text-gray-700 py-2 px-4 rounded-lg text-sm font-medium whitespace-nowrap cursor-pointer text-center">
                    Browse more
                  </Link>
                </div>
              </div>
            </div>

            {/* Floating Elements */}
            <div className="absolute -top-4 -right-4 bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-sm font-medium">
              Top Deal
            </div>
            <div className="absolute -bottom-4 -left-4 bg-green-400 text-green-900 px-3 py-1 rounded-full text-sm font-medium">
              Verified Seller
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;